import * as Sentry from "@sentry/react";
import { type Query, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "components/ErrorBoundary";
import { LoaderLogo } from "components/Loader";
import { env } from "config/env";
import { authRoutes } from "domains/auth/views/Root/routes";
import React from "react";
import { Outlet, createBrowserRouter } from "react-router";
import { RouterProvider } from "react-router/dom";
import { captureException } from "utils/errorReporting";
import { makeLazyRoute } from "utils/routing";
import { ModeGuard } from "./ModeGuard";
import { ROUTES } from "./Routes";

const MessagingChatTokenProvider = React.lazy(() =>
  import("domains/messaging/providers/Auth").then(({ MessagingChatTokenProvider }) => ({
    default: MessagingChatTokenProvider,
  })),
);

const onError = (error: unknown, data: any) => {
  if (error instanceof Error && error.message.includes("Zod")) {
    const query = "queryKey" in data ? (data as Query) : undefined;
    const extra = query ? { queryKey: query.queryKey } : { variables: data };
    captureException(error, { extra });
  }
};

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  defaultOptions: { queries: { staleTime: 10 * 1000, retry: 1 }, mutations: { onError } },
});

const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);
const router = sentryCreateBrowserRouter([
  {
    path: "*",
    HydrateFallback: LoaderLogo,
    loader: async () => {
      const auth = await import("domains/auth/initializeAuth");
      auth.initializeAuth();
    },
    lazy: makeLazyRoute(() => import("providers/Root"), "RootProvider"),
    ErrorBoundary,
    children: [
      {
        path: `${ROUTES.SHIFT_MESSAGES.$path({ relative: true })}/*`,
        element: (
          <MessagingChatTokenProvider>
            <Outlet />
          </MessagingChatTokenProvider>
        ),
        children: [{ path: "*", lazy: makeLazyRoute(() => import("domains/messaging/views/Home"), "Home") }],
      },
      {
        path: `${authRoutes.$path({ relative: true })}/*`,
        lazy: makeLazyRoute(() => import("domains/auth/views/Root"), "AuthRootView"),
      },
      ...(env.IS_LOCAL ?
        [{ path: "login", lazy: makeLazyRoute(() => import("containers/Login"), "LoginContainer") }]
      : []),
      {
        path: "*",
        lazy: makeLazyRoute(() => import("domains/auth/providers/Root"), "AuthProvider"),
        children: [
          {
            path: ROUTES.SIGN_UP.$path({ relative: true }),
            lazy: makeLazyRoute(() => import("domains/signUp/views/SignUpPage"), "SignUpPage"),
          },
          {
            path: ROUTES.ACCEPT_LOCATION_INVITATION.$path({ relative: true }),
            lazy: makeLazyRoute(
              () => import("domains/locations/containers/AcceptManagerInvite"),
              "AcceptManagerInviteContainer",
            ),
          },
          {
            path: ROUTES.ACCEPT_ORG_INVITATION.$path({ relative: true }),
            lazy: makeLazyRoute(
              () => import("domains/locations/containers/AcceptManagerInvite"),
              "AcceptManagerInviteContainer",
            ),
          },
          {
            path: ROUTES.ACCOUNT_LINKING.$path({ relative: true }),
            lazy: makeLazyRoute(() => import("domains/signUp/containers/AccountLinking"), "AccountLinkingContainer"),
          },
          {
            path: `${ROUTES.CONNECT.$path({ relative: true })}/*`,
            lazy: makeLazyRoute(() => import("domains/connect/views/Limited"), "ConnectLimited"),
          },
          {
            path: "*",
            lazy: makeLazyRoute(() => import("./SignedUpRouter"), "SignedUpRouter"),
            children: [
              {
                path: `${ROUTES.ORGANIZATIONS.$path({ relative: true })}/*`,
                children: [
                  {
                    path: `${ROUTES.ORGANIZATIONS.$.DETAILS.$path({ relative: true })}/*`,
                    lazy: makeLazyRoute(() => import("domains/orgs/views/ManageOrganization"), "ManageOrganization"),
                  },
                  {
                    path: ROUTES.LOCATIONS.DETAILS.$.TEAM.$path({ relative: true }),
                    lazy: makeLazyRoute(() => import("domains/orgs/views/ManageOrganization"), "ManageOrganization"),
                  },
                  {
                    path: ROUTES.LOCATIONS.DETAILS.$.BILLING.$path({ relative: true }),
                    lazy: makeLazyRoute(() => import("domains/orgs/views/ManageOrganization"), "ManageOrganization"),
                  },
                ],
              },
              {
                path: `${ROUTES.LOCATIONS.$path({ relative: true })}/*`,
                children: [
                  {
                    index: true,
                    lazy: makeLazyRoute(
                      () => import("domains/orgs/views/OrganizationOverview"),
                      "OrganizationOverviewView",
                    ),
                  },
                  {
                    path: ROUTES.LOCATIONS.$.ADD.$path({ relative: true }),
                    lazy: makeLazyRoute(
                      () => import("domains/orgs/views/OrganizationOverview"),
                      "OrganizationOverviewView",
                    ),
                  },
                  {
                    path: ROUTES.LOCATIONS.$.DETAILS.$path({ relative: true }),
                    children: [
                      {
                        path: ROUTES.LOCATIONS.DETAILS.$.TEAM.$path({ relative: true }),
                        lazy: makeLazyRoute(() => import("domains/locations/views/ManageLocation"), "ManageLocation"),
                      },
                      {
                        path: ROUTES.LOCATIONS.DETAILS.$.INFO.$path({ relative: true }),
                        lazy: makeLazyRoute(() => import("domains/locations/views/ManageLocation"), "ManageLocation"),
                      },
                      {
                        path: ROUTES.LOCATIONS.DETAILS.$.BILLING.$path({ relative: true }),
                        lazy: makeLazyRoute(() => import("domains/locations/views/ManageLocation"), "ManageLocation"),
                      },
                      {
                        path: ROUTES.LOCATIONS.DETAILS.$.ADDONS.$path({ relative: true }),
                        lazy: makeLazyRoute(() => import("domains/locations/views/LocationAddOns"), "LocationAddOns"),
                      },
                      {
                        path: ROUTES.LOCATIONS.DETAILS.$.ORG_REMAP.$path({ relative: true }),
                        lazy: makeLazyRoute(
                          () => import("domains/locations/views/LocationOrgRemap"),
                          "LocationOrgRemap",
                        ),
                      },
                      {
                        path: "*",
                        Component: ModeGuard,
                      },
                    ],
                  },
                ],
              },
              {
                path: ROUTES.NOTIFICATION_SETTINGS.$path({ relative: true }),
                lazy: makeLazyRoute(
                  () => import("domains/admin/views/SmsNotificationSettings"),
                  "SmsNotificationSettings",
                ),
              },
              {
                path: ROUTES.SETTINGS.$path({ relative: true }),
                lazy: makeLazyRoute(() => import("domains/settings/views/Root"), "SettingsRootView"),
              },
              {
                path: `${ROUTES.ADMIN.$path({ relative: true })}/*`,
                lazy: makeLazyRoute(() => import("domains/admin/views/Router"), "AdminRouter"),
              },
              {
                path: "*",
                Component: ModeGuard,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export const Router = React.memo(function Router() {
  return (
    <React.Suspense fallback={<LoaderLogo />}>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </React.Suspense>
  );
});
